import { Skeleton } from '@mui/material';
import { PropsWithChildren } from 'react';
import { useIntl } from 'react-intl';

import { useIndexDateView } from '@verantos-dev/app-state-cohort-builder';

import { CohortBuilderInfoCard } from '../../../../components/CohortBuilderInfoCard/CohortBuilderInfoCard';
import { CohortSection } from '../../../../components/CohortSection/CohortSection';
import { useCohortBuilderModalContext } from '../../../../contexts/CohortBuilderModalContext';
import { useEmitResearchAssistantUpdate } from '../../../../hooks/common/useResearchAssistantUpdateForStudyPeriodCard';
import IndexDateEditCard from '../../cohort-builder-edit-cards/IndexDateEditCard/IndexDateEditCard';

type IndexDateSectionProps = {
  minimalMode?: boolean;
};

export const IndexDateSection = ({ minimalMode }: IndexDateSectionProps) => {
  const intl = useIntl();

  const {
    isIndexDateDefined,
    showIndexDate,
    isInUse,
    inUseItems,
    viewMode,
    isLoading,

    fields,
    isSetIndexDateButtonDisabled,
    showSetIndexDateButton,

    onClickSetIndexDate,
    onClickEditIndexDate,
    onClickDeleteIndexDate,
  } = useIndexDateView(minimalMode);
  const { activeDialog } = useCohortBuilderModalContext();
  const emitResearchAssistantUpdate = useEmitResearchAssistantUpdate(viewMode);

  const inUseIconTooltip = isInUse ? (
    <>
      <div>Used by:</div>
      {inUseItems?.map((item, index) => (
        <div key={item} id={`inUseItem-${index}`}>
          {` - ${item}`}
        </div>
      ))}
    </>
  ) : (
    'This item is not being used.'
  );

  const deleteTooltip = isInUse
    ? 'Please remove all references to this item  before deleting.'
    : 'Delete';

  const headerProps = {
    title: 'Index date',
    action: showSetIndexDateButton
      ? {
          label: intl.formatMessage({
            id: 'projects.cohortBuilderPage.indexDateViewCard.setIndexDateBtnLabel',
          }),
          labelIcon: 'calender',
          onClick: onClickSetIndexDate,
          disabled: isSetIndexDateButtonDisabled || activeDialog !== null,
        }
      : undefined,
  };

  const emptyStateProps = {
    id: 'index-date',
    mainMessage: intl.formatMessage({
      id: 'projects.cohortBuilderPage.indexDateViewCard.emptyMainMsg',
    }),
    subMessage: intl.formatMessage({
      id: 'projects.cohortBuilderPage.indexDateViewCard.emptySubMsg',
    }),
  };

  const Wrapper = ({ children }: PropsWithChildren) =>
    minimalMode ? (
      children
    ) : (
      <CohortSection key={'index-date'} headerProps={headerProps} emptyStateProps={emptyStateProps}>
        {children}
      </CohortSection>
    );

  if (isLoading) {
    return (
      <Wrapper>
        <Skeleton variant="rectangular" height={78} />
      </Wrapper>
    );
  }

  return (
    <>
      <Wrapper>
        {(isIndexDateDefined || minimalMode) && (
          <CohortBuilderInfoCard
            id="cohort-builder-index-date-info-card"
            info={fields}
            isTickIconEnabled={isInUse}
            tickIconTooltip={inUseIconTooltip}
            deleteTooltip={deleteTooltip}
            onEditClick={onClickEditIndexDate}
            onDeleteClick={
              isIndexDateDefined && !viewMode
                ? () => onClickDeleteIndexDate(emitResearchAssistantUpdate)
                : undefined
            }
            viewMode={viewMode}
            hideEdit={viewMode}
            minimalMode={minimalMode}
            showTickIcon={isIndexDateDefined}
          />
        )}
      </Wrapper>

      {showIndexDate && <IndexDateEditCard />}
    </>
  );
};
